import { type AriaProps } from '@common/components/controls/ariaHelpers/types';

/**
 * State for generating button ARIA attributes.
 * Following W3C Button pattern: https://www.w3.org/WAI/ARIA/apg/patterns/button/
 */
export interface ButtonState {
  disabled?: boolean;
  isLoading?: boolean;
  ariaLabel?: string;
  forbidden?: boolean;
}

/**
 * Generates ARIA attributes for a button element.
 *
 * @param state - The current button state
 * @returns ARIA props to spread onto the button element
 */
export function getButtonAriaProps(state: ButtonState): AriaProps {
  const { disabled, isLoading, ariaLabel, forbidden } = state;

  const props: AriaProps = {};

  // Use aria-disabled for consistency with screen readers
  // Note: Still use disabled attribute for native form functionality
  if (disabled || forbidden || isLoading) {
    props['aria-disabled'] = true;
  }

  // Communicate loading state to screen readers
  if (isLoading) {
    props['aria-busy'] = true;
  }

  // Icon-only buttons require aria-label
  if (ariaLabel) {
    props['aria-label'] = ariaLabel;
  }

  return props;
}

/**
 * Generates unique ID for error message element.
 * Used with aria-describedby to link button to error message.
 *
 * @param buttonId - The ID of the button element
 * @returns ID string for the error message element
 */
export function getButtonErrorId(buttonId: string): string {
  return `${buttonId}-error`;
}
